import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  createPlaybackAnchor,
  expectedPlaybackPosition,
  type MediaPlayback,
} from "@yay/shared";
import { formatTime, loadYouTubeApi } from "../lib/youtube";
import {
  PLAYBACK_RATES,
  ensureCaptionsModule,
  formatPlaybackRate,
  readActiveCaptionTrack,
  readCaptionTracks,
  setCaptionEnabled,
  type CaptionTrackOption,
} from "../lib/youtube";

type PlayerState = "loading" | "playing" | "error";

interface YouTubePlayerProps {
  youtubeId: string;
  playback?: MediaPlayback;
  stowed?: boolean;
  /** Client↔server clock offset (ms) — compensates wall-clock anchor skew. */
  clockOffsetMs?: number;
  onSync: (sync: { playing?: boolean; positionSec?: number }) => void;
}

const DRIFT_THRESHOLD_SEC = 1.5;
const SYNC_INTERVAL_MS = 3_000;

/**
 * True when a wall-clock-anchored position has run past the video's known
 * length. Once a video ends, `expectedPlaybackPosition()` keeps counting up
 * because the anchor still says `playing: true`, so any seek to that position
 * clamps to the end and restarts the video from 0 — the reported loop.
 *
 * Plain English: the room's math says "we should be at second 600", but the
 * video is only 5 minutes long, so we must refuse to jump there.
 */
function isBeyondVideoEnd(player: YT.Player, positionSec: number): boolean {
  const dur = player.getDuration();
  return Number.isFinite(dur) && dur > 0 && positionSec > dur;
}

export function YouTubePlayer({
  youtubeId,
  playback,
  stowed = false,
  clockOffsetMs = 0,
  onSync,
}: YouTubePlayerProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YT.Player | null>(null);
  const initRef = useRef(0);
  const applyingRemoteRef = useRef(false);
  const scrubbingRef = useRef(false);
  /**
   * Compensated "now" for wall-clock-anchored playback math. The anchor's
   * `at` is SERVER time; extrapolating with raw client Date.now() inherits
   * device clock skew as a visible lag/lead. offset = serverTime − localNow
   * (estimated from the latest room_state's serverTime stamp).
   */
  const clockOffsetRef = useRef(clockOffsetMs);
  clockOffsetRef.current = clockOffsetMs;
  const nowWithOffset = (): number => Date.now() + clockOffsetRef.current;
  const pendingLocalSyncRef = useRef<{
    positionSec: number;
    playing: boolean;
  } | null>(null);
  const localSeekGuardRef = useRef<{
    positionSec: number;
    playing: boolean;
    at: number;
  } | null>(null);
  const stowedRef = useRef(stowed);
  const playbackRef = useRef(playback);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const captionPollRef = useRef<number | null>(null);
  const savedCaptionTrackRef = useRef<CaptionTrackOption | null>(null);
  const captionUserSetRef = useRef(false);

  const [playerState, setPlayerState] = useState<PlayerState>("loading");
  const [errorText, setErrorText] = useState("");
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(100);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [scrubValue, setScrubValue] = useState(0);
  const [showVolume, setShowVolume] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [captionTracks, setCaptionTracks] = useState<CaptionTrackOption[]>([]);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showCcMenu, setShowCcMenu] = useState(false);

  stowedRef.current = stowed;
  playbackRef.current = playback;

  const applyRemotePlayback = useCallback((player: YT.Player, pb: MediaPlayback) => {
    applyingRemoteRef.current = true;
    const target = expectedPlaybackPosition(pb, nowWithOffset());
    player.seekTo(target, true);
    if (pb.playing) {
      player.playVideo();
    } else {
      player.pauseVideo();
    }
    setPlaying(pb.playing);
    setCurrentTime(target);
    setScrubValue(target);
    window.setTimeout(() => {
      applyingRemoteRef.current = false;
    }, 400);
  }, []);

  const broadcastSync = useCallback(
    (sync: { playing?: boolean; positionSec?: number }) => {
      onSync(sync);
    },
    [onSync],
  );

  const clearLocalSeekGuard = useCallback(() => {
    pendingLocalSyncRef.current = null;
    localSeekGuardRef.current = null;
  }, []);

  const markLocalSync = useCallback(
    (positionSec: number, isPlaying: boolean) => {
      const sync = { positionSec, playing: isPlaying };
      pendingLocalSyncRef.current = sync;
      localSeekGuardRef.current = { ...sync, at: Date.now() };
      applyingRemoteRef.current = true;
      window.setTimeout(() => {
        applyingRemoteRef.current = false;
      }, 1200);
      // Safety net: `pendingLocalSyncRef` was only ever cleared when the room
      // echoed an anchor matching our local seek. If another user's sync
      // overwrote it (or the echo was dropped), we would keep ignoring every
      // remote playback update forever and silently desync. Force-clear after
      // 2s — a matching echo arrives in well under that on a healthy socket.
      //
      // Plain English: if we pause/seek and the room never "agrees" with us,
      // stop ignoring everyone else's sync after 2s so we don't stay out of
      // sync indefinitely.
      window.setTimeout(() => clearLocalSeekGuard(), 2_000);
    },
    [clearLocalSeekGuard],
  );

  const shouldIgnoreRemotePlayback = useCallback((pb: MediaPlayback): boolean => {
    const guard = localSeekGuardRef.current;
    if (!guard) return false;

    const posMatch = Math.abs(pb.positionSec - guard.positionSec) < 0.35;
    const playMatch = pb.playing === guard.playing;
    if (posMatch && playMatch) {
      clearLocalSeekGuard();
      return true;
    }
    return false;
  }, [clearLocalSeekGuard]);

  const shouldApplyRemotePlayback = useCallback((pb: MediaPlayback): boolean => {
    const guard = localSeekGuardRef.current;
    if (!guard) return true;

    if (shouldIgnoreRemotePlayback(pb)) {
      return false;
    }

    const expected = expectedPlaybackPosition(pb, nowWithOffset());
    if (
      Math.abs(expected - guard.positionSec) > 1.5 ||
      Math.abs(pb.positionSec - guard.positionSec) > 1.5
    ) {
      return false;
    }

    return true;
  }, [shouldIgnoreRemotePlayback]);

  /**
   * True when this viewer's player runs at a non-1× speed. The shared
   * playhead math assumes 1× — a speed-changed viewer drifts from the
   * anchor, and the room's corrections would snap them back every 3s while
   * their own re-anchor would yank the whole room to their 2× position.
   * While off 1×, this player neither applies remote sync nor broadcasts
   * re-anchors: it's "off the leash" (local speed control works), and
   * resetting to 1× re-joins the room on the next drift check.
   */
  const rateDeviated = useCallback((): boolean => {
    const p = playerRef.current;
    return p ? Math.abs(p.getPlaybackRate() - 1) > 0.001 : playbackRate !== 1;
  }, [playbackRate]);

  const stopCaptionPoll = useCallback(() => {
    if (captionPollRef.current) {
      window.clearTimeout(captionPollRef.current);
      captionPollRef.current = null;
    }
  }, []);

  const refreshCaptionTracks = useCallback((player: YT.Player) => {
    const tracks = readCaptionTracks(player);
    setCaptionTracks(tracks);
    return tracks.length;
  }, []);

  const applyDefaultCaptions = useCallback((player: YT.Player) => {
    if (captionUserSetRef.current) return;

    const active = readActiveCaptionTrack(player);
    if (active) {
      savedCaptionTrackRef.current = active;
      setCaptionsEnabled(true);
      return;
    }

    const tracks = readCaptionTracks(player);
    if (tracks.length === 0) return;

    const track = tracks[0];
    if (!track) return;
    savedCaptionTrackRef.current = track;
    if (setCaptionEnabled(player, true, track)) {
      setCaptionsEnabled(true);
    }
  }, []);

  const pollCaptionTracks = useCallback(
    (player: YT.Player, attempt = 0) => {
      stopCaptionPoll();
      if (attempt === 0) ensureCaptionsModule(player);
      const found = refreshCaptionTracks(player);
      applyDefaultCaptions(player);
      if (found > 0 || attempt >= 12) {
        window.setTimeout(() => {
          if (playerRef.current !== player) return;
          applyDefaultCaptions(player);
        }, 400);
        return;
      }

      captionPollRef.current = window.setTimeout(() => {
        if (playerRef.current !== player) return;
        pollCaptionTracks(player, attempt + 1);
      }, 500);
    },
    [applyDefaultCaptions, refreshCaptionTracks, stopCaptionPoll],
  );

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const runId = ++initRef.current;
    setPlayerState("loading");
    setErrorText("");
    setPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    setScrubValue(0);
    setPlaybackRate(1);
    setCaptionTracks([]);
    setCaptionsEnabled(false);
    savedCaptionTrackRef.current = null;
    captionUserSetRef.current = false;
    setShowSpeedMenu(false);
    setShowCcMenu(false);

    let player: YT.Player | null = null;
    let tickTimer: number | null = null;

    void (async () => {
      try {
        await loadYouTubeApi();
      } catch {
        if (runId !== initRef.current) return;
        setPlayerState("error");
        setErrorText("Failed to load YouTube player.");
        return;
      }

      if (runId !== initRef.current || !mountRef.current) return;

      player = new YT.Player(mountRef.current, {
        videoId: youtubeId,
        width: "100%",
        height: "100%",
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          cc_load_policy: 1,
          playsinline: 1,
          origin: window.location.origin,
        },
        events: {
          onApiChange: (event) => {
            if (runId !== initRef.current) return;
            ensureCaptionsModule(event.target);
            refreshCaptionTracks(event.target);
            applyDefaultCaptions(event.target);
          },
          onReady: (event) => {
            if (runId !== initRef.current) return;
            const p = event.target;
            playerRef.current = p;
            const dur = p.getDuration();
            if (Number.isFinite(dur) && dur > 0) setDuration(dur);

            pollCaptionTracks(p);
            p.setPlaybackRate(1);

            const pb =
              playbackRef.current ?? createPlaybackAnchor(0, true);
            applyRemotePlayback(p, pb);

            if (!stowedRef.current && pb.playing) {
              p.playVideo();
            } else if (stowedRef.current) {
              p.pauseVideo();
            }

            setPlayerState("playing");
            setMuted(p.isMuted());
            setVolume(p.getVolume());

            tickTimer = window.setInterval(() => {
              if (runId !== initRef.current || !playerRef.current) return;
              if (scrubbingRef.current) return;
              const t = playerRef.current.getCurrentTime();
              setCurrentTime(t);
              setScrubValue(t);
            }, 500);
          },
          onStateChange: (event) => {
            if (runId !== initRef.current) return;
            const state = event.data;
            if (state === YT.PlayerState.PLAYING) {
              setPlaying(true);
              refreshCaptionTracks(event.target);
              applyDefaultCaptions(event.target);
            } else if (state === YT.PlayerState.ENDED) {
              setPlaying(false);
              // Previously the ENDED state only flipped the local `playing`
              // flag; the room's shared anchor kept `playing: true` forever, so
              // the 3s drift check kept seeking past the end (clamping to the
              // end + `playVideo()` restarting from 0). Broadcast the end so
              // the anchor becomes static `{playing: false, at: end}` for
              // everyone — this is the primary fix for the 0→1→0 loop.
              //
              // Plain English: when a video finishes, we now announce "it's
              // over, it's paused at the end" to the room instead of leaving
              // it thinking the video is still playing forever.
              const p = event.target;
              const dur = p.getDuration();
              const endPos = Number.isFinite(dur) && dur > 0 ? dur : p.getCurrentTime();
              broadcastSync({ playing: false, positionSec: endPos });
            } else if (
              state === YT.PlayerState.PAUSED ||
              state === YT.PlayerState.CUED ||
              state === YT.PlayerState.UNSTARTED
            ) {
              setPlaying(false);
            }
          },
          onError: () => {
            if (runId !== initRef.current) return;
            setPlayerState("error");
            setErrorText("This video can't be played here.");
          },
        },
      });
    })();

    return () => {
      stopCaptionPoll();
      if (tickTimer) window.clearInterval(tickTimer);
      player?.destroy();
      playerRef.current = null;
    };
  }, [
    youtubeId,
    applyRemotePlayback,
    applyDefaultCaptions,
    pollCaptionTracks,
    refreshCaptionTracks,
    stopCaptionPoll,
    broadcastSync,
  ]);

  // Apply remote playback updates from room state.
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playback || playerState !== "playing") return;
    if (scrubbingRef.current) return;
    if (rateDeviated()) return;

    if (pendingLocalSyncRef.current) {
      shouldIgnoreRemotePlayback(playback);
      return;
    }

    if (applyingRemoteRef.current) return;
    if (!shouldApplyRemotePlayback(playback)) return;
    if (shouldIgnoreRemotePlayback(playback)) return;

    const local = player.getCurrentTime();
    const expected = expectedPlaybackPosition(playback, nowWithOffset());
    // A stale "still playing" anchor runs past the video's end once the video
    // has finished. Seeking there clamps to the end and `playVideo()` restarts
    // from 0 — the loop. Previously this effect happily applied those seeks;
    // now it refuses any remote position beyond the known duration.
    //
    // Plain English: don't apply a remote "jump" that lands past the end of
    // the video — it can only cause the end→restart loop.
    if (isBeyondVideoEnd(player, expected)) return;
    const playMismatch = playback.playing !== playing;
    if (
      playMismatch ||
      Math.abs(local - expected) > DRIFT_THRESHOLD_SEC
    ) {
      applyRemotePlayback(player, playback);
    }
  }, [
    playback?.at,
    playback?.playing,
    playback?.positionSec,
    playerState,
    playing,
    applyRemotePlayback,
    playback,
    shouldIgnoreRemotePlayback,
    shouldApplyRemotePlayback,
    rateDeviated,
  ]);

  // Periodic drift correction for long plays.
  useEffect(() => {
    if (playerState !== "playing" || !playback) return;
    const id = window.setInterval(() => {
      const player = playerRef.current;
      if (!player || scrubbingRef.current) return;
      // Off 1× speed: leave this viewer alone (see rateDeviated).
      if (rateDeviated()) return;

      const pb = playbackRef.current;
      if (!pb) return;

      // An ended video must not be auto-resumed by a stale anchor — that is
      // what made it loop "play a second, jump to the end, restart from 0".
      if (player.getPlayerState() === YT.PlayerState.ENDED) return;

      if (pendingLocalSyncRef.current) {
        shouldIgnoreRemotePlayback(pb);
        return;
      }

      if (applyingRemoteRef.current) return;
      if (!shouldApplyRemotePlayback(pb)) return;

      const expected = expectedPlaybackPosition(pb, nowWithOffset());
      const actual = player.getCurrentTime();
      if (isBeyondVideoEnd(player, expected)) return;
      const guard = localSeekGuardRef.current;
      if (
        guard &&
        Math.abs(actual - guard.positionSec) < 1 &&
        Math.abs(expected - guard.positionSec) > DRIFT_THRESHOLD_SEC
      ) {
        return;
      }
      if (Math.abs(actual - expected) > DRIFT_THRESHOLD_SEC) {
        applyRemotePlayback(player, pb);
      }
    }, SYNC_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [
    playerState,
    playback,
    applyRemotePlayback,
    shouldApplyRemotePlayback,
    shouldIgnoreRemotePlayback,
    rateDeviated,
  ]);

  // Periodic re-anchor: the shared anchor only advances by wall clock from
  // the last explicit sync (play/pause/scrub/end). Over a long watch the
  // controller's REAL playback drifts from that extrapolation (buffering,
  // tab throttling), so every viewer's corrected position lags behind what
  // is actually on screen. While playing, re-assert the real position every
  // 10s — but only on genuine disagreement, so the room_state fan-out stays
  // low when everyone is already in sync.
  useEffect(() => {
    if (playerState !== "playing") return;
    const id = window.setInterval(() => {
      const player = playerRef.current;
      if (!player || stowedRef.current || scrubbingRef.current) return;
      // A speed-changed viewer must not re-anchor the room to their 2×
      // position — every viewer's playhead would be yanked along.
      if (rateDeviated()) return;
      if (pendingLocalSyncRef.current || applyingRemoteRef.current) return;
      const state = player.getPlayerState();
      if (state !== YT.PlayerState.PLAYING && state !== YT.PlayerState.PAUSED)
        return;
      const pb = playbackRef.current;
      if (!pb) return;
      const playingNow = state === YT.PlayerState.PLAYING;
      const expected = expectedPlaybackPosition(pb, nowWithOffset());
      const local = player.getCurrentTime();
      if (Math.abs(local - expected) < 0.75 && playingNow === pb.playing)
        return;
      broadcastSync({ playing: playingNow, positionSec: local });
    }, 10_000);
    return () => window.clearInterval(id);
  }, [playerState, broadcastSync, rateDeviated]);

  // Pause while stowed; resume when revealed.
  const wasStowedRef = useRef(stowed);
  useEffect(() => {
    const wasStowed = wasStowedRef.current;
    wasStowedRef.current = stowed;
    const player = playerRef.current;
    if (!player || playerState !== "playing") return;

    if (stowed) {
      if (playing) player.pauseVideo();
      return;
    }
    if (!wasStowed) return;

    const pb = playbackRef.current;
    if (pb) {
      if (!isBeyondVideoEnd(player, expectedPlaybackPosition(pb, nowWithOffset()))) {
        applyRemotePlayback(player, pb);
      }
    } else player.playVideo();
  }, [stowed, playerState, playing, applyRemotePlayback]);

  const handleUnmute = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    player.unMute();
    setMuted(false);
    if (playbackRef.current?.playing) player.playVideo();
  }, []);

  const togglePlay = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    // After a video ends, getCurrentTime() returns the duration. Restarting
    // from "the end" would leave the shared anchor there and re-trigger the
    // end→restart loop, so use 0 when replaying an ended video.
    const ended = player.getPlayerState() === YT.PlayerState.ENDED;
    const pos = ended ? 0 : player.getCurrentTime();
    if (playing) {
      setPlaying(false);
      markLocalSync(pos, false);
      player.pauseVideo();
      broadcastSync({ playing: false, positionSec: pos });
    } else {
      setPlaying(true);
      markLocalSync(pos, true);
      player.playVideo();
      broadcastSync({ playing: true, positionSec: pos });
    }
  }, [playing, broadcastSync, markLocalSync]);

  const toggleMute = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    if (player.isMuted()) {
      player.unMute();
      setMuted(false);
    } else {
      player.mute();
      setMuted(true);
    }
  }, []);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const player = playerRef.current;
      if (!player) return;
      const v = Math.round(parseFloat(e.target.value) * 100);
      player.setVolume(v);
      setVolume(v);
      if (v > 0 && player.isMuted()) {
        player.unMute();
        setMuted(false);
      }
    },
    [],
  );

  const handleScrubStart = useCallback(() => {
    scrubbingRef.current = true;
  }, []);

  const handleScrubChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      setScrubValue(v);
      setCurrentTime(v);
    },
    [],
  );

  const commitScrub = useCallback(
    (
      e:
        | React.PointerEvent<HTMLInputElement>
        | React.KeyboardEvent<HTMLInputElement>,
    ) => {
      scrubbingRef.current = false;
      const player = playerRef.current;
      if (!player) return;
      const pos = parseFloat(e.currentTarget.value);
      setScrubValue(pos);
      setCurrentTime(pos);
      const isPlaying = player.getPlayerState() === YT.PlayerState.PLAYING;
      markLocalSync(pos, isPlaying);
      player.seekTo(pos, true);
      broadcastSync({ playing: isPlaying, positionSec: pos });
    },
    [broadcastSync, markLocalSync],
  );

  const toggleFullscreen = useCallback(() => {
    const el = mountRef.current?.parentElement;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  const selectPlaybackRate = useCallback((rate: number) => {
    const player = playerRef.current;
    if (!player) return;
    player.setPlaybackRate(rate);
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
  }, []);

  const setCaptions = useCallback((enabled: boolean, track?: CaptionTrackOption) => {
    const player = playerRef.current;
    if (!player) return;
    captionUserSetRef.current = true;

    if (!enabled) {
      const active = readActiveCaptionTrack(player);
      if (active) savedCaptionTrackRef.current = active;
      setCaptionsEnabled(false);
      setCaptionEnabled(player, false);
      setShowCcMenu(false);
      return;
    }

    const trackToUse =
      track ??
      savedCaptionTrackRef.current ??
      readCaptionTracks(player)[0] ??
      null;

    setCaptionsEnabled(true);
    if (trackToUse) savedCaptionTrackRef.current = trackToUse;
    setCaptionEnabled(player, true, trackToUse);
    setShowCcMenu(false);
  }, []);

  const toggleCcMenu = useCallback(() => {
    setShowSpeedMenu(false);
    setShowCcMenu((open) => {
      const next = !open;
      if (next) {
        const player = playerRef.current;
        if (player) pollCaptionTracks(player);
      }
      return next;
    });
  }, [pollCaptionTracks]);

  const closeMenus = useCallback(() => {
    setShowSpeedMenu(false);
    setShowCcMenu(false);
    setShowVolume(false);
  }, []);

  const scheduleHideControls = useCallback(() => {
    window.clearTimeout(hideTimerRef.current);
    setShowControls(true);
    hideTimerRef.current = setTimeout(() => {
      setShowControls(false);
      closeMenus();
    }, 2500);
  }, [closeMenus]);

  useEffect(() => {
    return () => window.clearTimeout(hideTimerRef.current);
  }, []);

  const isMutedOrSilent = muted || volume === 0;
  const progressMax = duration > 0 ? duration : Math.max(scrubValue, 1);
  const menuOpen = showCcMenu || showSpeedMenu;

  return (
    <div
      className={`player-container youtube${showControls ? " controls-visible" : ""}${menuOpen ? " controls-menu-open" : ""}`}
      onMouseMove={scheduleHideControls}
      onMouseLeave={() => {
        setShowControls(false);
        closeMenus();
      }}
    >
      <div ref={mountRef} className="youtube-mount" />
      <button
        type="button"
        className="youtube-click-layer"
        onClick={togglePlay}
        aria-label={playing ? "Pause" : "Play"}
      />

      {playerState === "loading" && (
        <div className="player-overlay player-loading">
          <div className="player-glow" />
          <div className="player-spinner" />
          <p>Loading video...</p>
        </div>
      )}

      {playerState === "playing" && isMutedOrSilent && (
        <button
          className="player-overlay player-unmute"
          onClick={handleUnmute}
          aria-label="Unmute video"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 5 6 9 2 9 2 15 6 15 11 19 11 5Z" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
          <span>Click to unmute</span>
        </button>
      )}

      {playerState === "error" && (
        <div className="player-overlay player-error">
          <div className="player-error-badge">!</div>
          <p>{errorText}</p>
        </div>
      )}

      {playerState === "playing" && (
        <div
          className={`player-controls player-controls-vod${menuOpen ? " controls-menu-open" : ""}`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="progress-row">
            <input
              type="range"
              className="progress-slider"
              min={0}
              max={progressMax}
              step={0.1}
              value={Math.min(scrubValue, progressMax)}
              onPointerDown={handleScrubStart}
              onMouseDown={handleScrubStart}
              onTouchStart={handleScrubStart}
              onChange={handleScrubChange}
              onPointerUp={commitScrub}
              onKeyUp={commitScrub}
              onClick={(e) => e.stopPropagation()}
              aria-label="Seek"
            />
            <span className="time-label">
              {formatTime(currentTime)}
              {duration > 0 ? ` / ${formatTime(duration)}` : ""}
            </span>
          </div>

          <div className="controls-row">
            <div className="controls-left">
              <button
                className="control-btn"
                onClick={togglePlay}
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="6,3 20,12 6,21" />
                  </svg>
                )}
              </button>

              <span className="live-badge sync-badge">
                <span className="live-dot" />
                SYNC
              </span>
            </div>

            <div className="controls-right">
              <div className="player-menu-group">
                <button
                  type="button"
                  className={`control-btn control-text${playbackRate !== 1 ? " active" : ""}`}
                  onClick={() => {
                    setShowCcMenu(false);
                    setShowSpeedMenu((v) => !v);
                  }}
                  aria-label="Playback speed"
                  aria-expanded={showSpeedMenu}
                >
                  {formatPlaybackRate(playbackRate)}
                </button>
                {showSpeedMenu && (
                  <div className="player-menu" role="menu">
                    {PLAYBACK_RATES.map((rate) => (
                      <button
                        key={rate}
                        type="button"
                        role="menuitemradio"
                        aria-checked={playbackRate === rate}
                        className={`player-menu-item${playbackRate === rate ? " active" : ""}`}
                        onClick={() => selectPlaybackRate(rate)}
                      >
                        {formatPlaybackRate(rate)}
                      </button>
                    ))}
                    {playbackRate !== 1 && (
                      <div className="player-menu-note" role="note">
                        room sync pauses off 1× — set 1× to rejoin
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="player-menu-group">
                <button
                  type="button"
                  className={`control-btn control-text${captionsEnabled ? " active" : ""}`}
                  onClick={toggleCcMenu}
                  aria-label="Captions"
                  aria-expanded={showCcMenu}
                >
                  CC
                </button>
                {showCcMenu && (
                  <div className="player-menu" role="menu">
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={!captionsEnabled}
                      className={`player-menu-item${!captionsEnabled ? " active" : ""}`}
                      onClick={() => setCaptions(false)}
                    >
                      Off
                    </button>
                    {captionTracks.length > 0 ? (
                      captionTracks.map((track) => (
                        <button
                          key={`${track.languageCode}:${track.label}`}
                          type="button"
                          role="menuitemradio"
                          aria-checked={captionsEnabled}
                          className={`player-menu-item${captionsEnabled ? " active" : ""}`}
                          onClick={() => setCaptions(true, track)}
                        >
                          {track.label}
                        </button>
                      ))
                    ) : (
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={captionsEnabled}
                        className={`player-menu-item${captionsEnabled ? " active" : ""}`}
                        onClick={() => setCaptions(true)}
                      >
                        On
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div
                className="volume-group"
                onMouseEnter={() => {
                  setShowSpeedMenu(false);
                  setShowCcMenu(false);
                  setShowVolume(true);
                }}
                onMouseLeave={() => setShowVolume(false)}
              >
                <button
                  className="control-btn"
                  onClick={toggleMute}
                  aria-label={isMutedOrSilent ? "Unmute" : "Mute"}
                >
                  {isMutedOrSilent ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 5 6 9 2 9 2 15 6 15 11 19 11 5Z" />
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 5 6 9 2 9 2 15 6 15 11 19 11 5Z" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </svg>
                  )}
                </button>
                <div className={`volume-slider-wrap${showVolume ? " open" : ""}`}>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={isMutedOrSilent ? 0 : volume / 100}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                    aria-label="Volume"
                  />
                </div>
              </div>

              <button
                className="control-btn"
                onClick={toggleFullscreen}
                aria-label="Fullscreen"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
